import { Injectable, inject } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ErrorHandlerService } from '../shared/services/error-handler.service';
import { ValidationErrorHandlerService } from '../shared/services/validation-error-handler.service';

/**
 * Interceptor obsługujący błędy HTTP
 * 
 * - Transformuje błędy do ustandaryzowanego formatu
 * - AUTOMATYCZNIE pokazuje toasty dla błędów walidacji
 * - Pozostałe błędy przekazuje do ErrorHandlerService (toast)
 * 
 * Field errors są obsługiwane przez @Input backendErrors w FormErrorMsgComponent
 */
@Injectable()
export class HttpErrorInterceptor implements HttpInterceptor {

  constructor(
    private _errorHandlerService: ErrorHandlerService,
    private _validationHandler: ValidationErrorHandlerService
  ) {}

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(request)
      .pipe(
        catchError((error: HttpErrorResponse) => {
          // Pomiń błędy ładowania translacji
          if (request.url.includes('/assets/i18n/')) {
            return throwError(() => error);
          }

          // Entitlements są pobierane w tle (UX-only) — cicha awaria,
          // bez globalnego toasta przy starcie aplikacji.
          if (request.url.includes('me/entitlements')) {
            return throwError(() => error);
          }

          // 401 Unauthorized - obsługiwane przez TokenRefreshInterceptor
          // NIE pokazuj toasta - token refresh może przywrócić sesję
          if (error.status === 401) {
            return throwError(() => error);
          }

          // Przekształć błąd do ustandaryzowanego formatu
          const transformedError = this.transformError(error);

          // Flow „Połącz z Facebookiem" (meta-login) — błędy (403/410/503)
          // obsługuje kontekstowo channel-dialog (własne toasty i stany),
          // bez globalnego toasta (uniknięcie podwójnych komunikatów).
          if (request.url.includes('/meta-login/')) {
            return throwError(() => transformedError);
          }

          // Limit planu (quota) — obsługiwany kontekstowo w danej funkcji
          // (np. baner + CTA w generatorze treści). Nie pokazuj globalnego
          // toasta, aby uniknąć podwójnego komunikatu.
          if (this.isQuotaError(transformedError)) {
            return throwError(() => transformedError);
          }

          // Sprawdź czy to błąd walidacji
          const isValidationError = this.isValidationError(transformedError);

          if (isValidationError) {
            // Pokaż TOAST (FIELD ERRORS obsługuje komponent przez backendErrors @Input)
            this._validationHandler.handleValidationError(transformedError);
          } else {
            // Inne błędy - obsługuje ErrorHandlerService
            this._errorHandlerService.catchErrorBackend(transformedError);
          }

          return throwError(() => transformedError);
        })
      );
  }
  
  /**
   * Sprawdź czy to błąd walidacji (pomijany w globalnym handlerze)
   */
  private isValidationError(error: any): boolean {
    return error.errorCode === 'VALIDATION.MULTIPLE_ERRORS'
      && !!error.validationErrors;
  }

  /**
   * Czy to błąd przekroczenia limitu planu (quota), np. CONTENT.QUOTA_EXCEEDED.
   * Takie błędy są obsługiwane kontekstowo (baner + CTA "podbij plan").
   */
  private isQuotaError(error: any): boolean {
    return typeof error?.errorCode === 'string'
      && error.errorCode.endsWith('.QUOTA_EXCEEDED');
  }
  
  /**
   * Kod błędu dla odpowiedzi bez ciała JSON (np. 413 ucięte przez nginx/proxy,
   * zanim żądanie dotarło do API). Dzięki temu komponenty i ErrorHandlerService
   * widzą ten sam errorCode, niezależnie od tego, kto odrzucił żądanie.
   */
  private fallbackErrorCode(status: number): string | undefined {
    return status === 413 ? 'COMMON.PAYLOAD_TOO_LARGE' : undefined;
  }

  /**
   * Przekształć HttpErrorResponse na ustandaryzowany format
   * Wyciąga dane z error.error (response body) na główny poziom
   */
  private transformError(error: HttpErrorResponse): any {
    // Body bywa stringiem (413 z nginx/proxy to HTML, nie JSON) — wtedy nie ma errorCode.
    const body = typeof error.error === 'object' ? error.error : null;

    return {
      status: error.status,
      statusText: error.statusText,
      url: error.url,
      // Wyciągnij dane z body
      errorCode: body?.errorCode ?? this.fallbackErrorCode(error.status),
      message: body?.message,
      validationErrors: body?.validationErrors,
      data: body?.data,
      errors: body?.errors,
      // Backward compatibility dla starych błędów
      tempToken: body?.tempToken,
      userId: body?.userId,
      mfaMethod: body?.mfaMethod
    };
  }
}
